import { useQuery } from "@tanstack/react-query";
import _ from "lodash";
import { useEffect, useState } from "react";

import useLabels from "../../../containers/FacilityDetailsPage/hooks/useLabels";
import { LabelData } from "../../../models/LabelData";
import { LocationData } from "../../../models/LocationData";
import DataSvc from "../../../services/dataSvc";

export const useLocationSelect = (organizationId?: string) => {
  const [selectedLabelIds, setSelectedLabelIds] = useState<number[]>([]);

  const { data: labels } = useLabels(
    _.pickBy({
      organization_id: organizationId,
    })
  );

  const query = useQuery(
    ["location-select", organizationId, selectedLabelIds],
    () =>
      DataSvc.getLocations(
        _.pickBy(
          {
            organization_id: organizationId ? [organizationId] : undefined,
            label_id:
              selectedLabelIds.length > 0 ? selectedLabelIds : undefined,
            summary_mode: true,
            limit: 10000,
          },
          (value: any) => {
            return !_.isUndefined(value) && !_.isNull(value);
          }
        )
      ),
    { keepPreviousData: true }
  );

  const [selectedLocations, setSelectedLocations] = useState<LocationData[]>(
    query.data?.list ?? []
  );

  // Select every location matching the current label filter whenever the
  // filtered list changes, so hidden locations are never left selected.
  useEffect(() => {
    setSelectedLocations(query.data?.list ?? []);
  }, [query.data]);

  const toggleLocation = (location: LocationData) => {
    setSelectedLocations(
      selectedLocations.some((l) => l.id === location.id)
        ? selectedLocations.filter((l) => l.id !== location.id)
        : [...selectedLocations, location]
    );
  };

  const toggleLabel = (label: LabelData) => {
    setSelectedLabelIds((prev) =>
      prev.includes(label.id)
        ? prev.filter((id) => id !== label.id)
        : [...prev, label.id]
    );
  };

  const clearLabels = () => {
    setSelectedLabelIds([]);
  };

  const selectAll = () => {
    setSelectedLocations(query.data?.list ?? []);
  };

  const clearSelection = () => {
    setSelectedLocations([]);
  };

  return {
    allLocations: query.data?.list ?? [],
    selectedLocations,
    toggleLocation,
    selectAll,
    clearSelection,
    labels: labels ?? [],
    selectedLabelIds,
    toggleLabel,
    clearLabels,
  };
};
